import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type AudienceMode = "patient" | "provider" | "both";

const STORAGE_KEY = "mrehab_audience";
const SESSION_KEY = "mrehab_audience_session";

function readStoredAudience(): AudienceMode | null {
  try {
    const fromLocal = localStorage.getItem(STORAGE_KEY) as AudienceMode | null;
    if (
      fromLocal === "patient" ||
      fromLocal === "provider" ||
      fromLocal === "both"
    ) {
      return fromLocal;
    }
    const fromSession = sessionStorage.getItem(SESSION_KEY) as AudienceMode | null;
    if (
      fromSession === "patient" ||
      fromSession === "provider" ||
      fromSession === "both"
    ) {
      return fromSession;
    }
  } catch {
    /* ignore */
  }
  return null;
}

type AudienceContextValue = {
  audience: AudienceMode | null;
  setAudience: (mode: AudienceMode, remember: boolean) => void;
  clearAudience: () => void;
};

const AudienceContext = createContext<AudienceContextValue | null>(null);

export function AudienceProvider({ children }: { children: React.ReactNode }) {
  const [audience, setAudienceState] = useState<AudienceMode | null>(() =>
    readStoredAudience()
  );

  const setAudience = useCallback((mode: AudienceMode, remember: boolean) => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(STORAGE_KEY);
      if (remember) {
        localStorage.setItem(STORAGE_KEY, mode);
      } else {
        sessionStorage.setItem(SESSION_KEY, mode);
      }
    } catch {
      /* ignore */
    }
    setAudienceState(mode);
  }, []);

  const clearAudience = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
    setAudienceState(null);
  }, []);

  const value = useMemo(
    () => ({ audience, setAudience, clearAudience }),
    [audience, setAudience, clearAudience]
  );

  return (
    <AudienceContext.Provider value={value}>{children}</AudienceContext.Provider>
  );
}

export function useAudience(): AudienceContextValue {
  const ctx = useContext(AudienceContext);
  if (!ctx) {
    throw new Error("useAudience must be used within AudienceProvider");
  }
  return ctx;
}

/** For navigation and layout when no choice was persisted yet. */
export function useEffectiveAudience(): AudienceMode {
  const { audience } = useAudience();
  return audience ?? "both";
}
